import { ethers } from "ethers";
import type { BaseStoredNote } from "../types";
import type { FlatJoinSplitV2OperationResult } from "../../joinsplit-v2/operationTypes";

/** Consolidate old trees without losing intermediate results if a later
 * migration fails. Actual ownership/proof verification remains in migrate(). */
export async function migrateRetiredBalance(params: {
  notes: BaseStoredNote[];
  amount: bigint;
  chainId: number;
  pool: string;
  token: string;
  currentTree: () => Promise<number>;
  migrate: (
    notes: BaseStoredNote[],
    treeIndex: number,
    commitments: string[],
  ) => Promise<FlatJoinSplitV2OperationResult>;
  signal?: AbortSignal;
}) {
  let workingNotes = [...params.notes];
  const completed: FlatJoinSplitV2OperationResult[] = [];
  const outputs: BaseStoredNote[] = [];
  let error: string | undefined;
  const eligible = () => {
    const unique = new Map<string, BaseStoredNote>();
    for (const stored of workingNotes) {
      const n = stored.note;
      if (
        stored.spent ||
        n.protocol !== "flat-joinsplit-v2" ||
        n.chainId !== params.chainId ||
        n.poolAddress?.toLowerCase() !== params.pool.toLowerCase() ||
        n.tokenData?.tokenAddress?.toLowerCase() !==
          params.token.toLowerCase() ||
        n.treeIndex === undefined ||
        !Number.isSafeInteger(n.treeIndex) ||
        n.treeIndex < 0 ||
        n.leafIndex < 0 ||
        n.value <= 0n ||
        !ethers.isHexString(n.commitment, 32)
      )
        continue;
      unique.set(n.commitment.toLowerCase(), stored);
    }
    return [...unique.values()];
  };
  try {
    if (
      eligible().reduce((sum, stored) => sum + stored.note.value, 0n) <
      params.amount
    )
      throw new Error(
        "Insufficient total private balance; no migration transaction was submitted",
      );
    // Bound work even if another operator repeatedly retires the destination.
    for (let step = 0; step < params.notes.length * 2 + 4; step++) {
      if (params.signal?.aborted) throw new Error("Migration cancelled");
      const tree = await params.currentTree();
      if (!Number.isSafeInteger(tree) || tree < 0)
        throw new Error("Invalid current tree");
      const available = eligible();
      const current = available.filter(
        (stored) => stored.note.treeIndex === tree,
      );
      if (
        current.reduce((sum, stored) => sum + stored.note.value, 0n) >=
        params.amount
      )
        break;
      const groups = new Map<number, BaseStoredNote[]>();
      for (const stored of available) {
        const index = stored.note.treeIndex!;
        if (index >= tree) continue;
        const group = groups.get(index) || [];
        group.push(stored);
        groups.set(index, group);
      }
      const candidates = [...groups.entries()]
        .map(([treeIndex, entries]) => {
          let value = 0n;
          const notes: BaseStoredNote[] = [];
          for (const stored of entries.sort((a, b) =>
            a.note.value > b.note.value
              ? -1
              : a.note.value < b.note.value
                ? 1
                : 0,
          )) {
            if (notes.length === 32) break;
            if (value + stored.note.value >= 1n << 120n) continue;
            notes.push(stored);
            value += stored.note.value;
          }
          return { treeIndex, notes, value };
        })
        .filter((group) => group.notes.length)
        .sort((a, b) =>
          a.value > b.value
            ? -1
            : a.value < b.value
              ? 1
              : b.treeIndex - a.treeIndex,
        );
      const group = candidates[0];
      if (!group) break;
      const result = await params.migrate(
        workingNotes,
        group.treeIndex,
        group.notes.map((stored) => stored.note.commitment),
      );
      if (!result.success || !result.outputNotes.length)
        throw new Error(
          result.error || "Old-note migration could not be completed",
        );
      completed.push(result);
      const spent = new Set(
        result.spentNotes.map((note) => note.commitment.toLowerCase()),
      );
      const fresh = [
        ...result.outputNotes,
        ...(result.gasChangeNote ? [result.gasChangeNote] : []),
      ].map((note) => ({
        note,
        timestamp: Date.now(),
        txHash: result.txHash,
      }));
      outputs.push(...fresh);
      workingNotes = workingNotes.map((stored) =>
        spent.has(stored.note.commitment.toLowerCase())
          ? { ...stored, spent: true }
          : stored,
      );
      const known = new Set(
        workingNotes.map((stored) => stored.note.commitment.toLowerCase()),
      );
      workingNotes.push(
        ...fresh.filter(
          (stored) => !known.has(stored.note.commitment.toLowerCase()),
        ),
      );
      if (step === params.notes.length * 2 + 3)
        error =
          "The active tree changed repeatedly. Sync your balance before retrying.";
    }
  } catch (failure) {
    if (!completed.length) throw failure;
    error = failure instanceof Error ? failure.message : String(failure);
  }
  if (!completed.length) return null;
  const spentNotes = completed.flatMap((result) => result.spentNotes);
  const spent = new Set(
    spentNotes.map((note) => note.commitment.toLowerCase()),
  );
  const migrationNotes = outputs.filter(
    (stored) => !spent.has(stored.note.commitment.toLowerCase()),
  );
  return {
    success: true,
    error,
    txHash: completed[completed.length - 1].txHash,
    spentNotes,
    outputNotes: migrationNotes.map((stored) => stored.note),
    migrationNotes,
    workingNotes,
    journalIds: completed
      .map((result) => result.journalId)
      .filter((id): id is string => Boolean(id)),
  };
}

/** Retain every confirmed migration output unless a later operation spent it. */
export function mergeMigrationNotes(
  previous: BaseStoredNote[],
  recovered: BaseStoredNote[],
  spentCommitments: string[] = [],
) {
  const spent = new Set(spentCommitments.map((value) => value.toLowerCase()));
  const merged = new Map(
    previous.map((stored) => [
      stored.note.commitment.toLowerCase(),
      spent.has(stored.note.commitment.toLowerCase())
        ? { ...stored, spent: true }
        : stored,
    ]),
  );
  for (const stored of recovered) {
    const commitment = stored.note.commitment.toLowerCase();
    if (!spent.has(commitment) && !merged.has(commitment))
      merged.set(commitment, stored);
  }
  return [...merged.values()];
}
